import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext, closestCorners, PointerSensor, KeyboardSensor, useSensor, useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS as dndCSS } from '@dnd-kit/utilities';
import {
  Plus, X, Sparkles, FileText, AlertTriangle, Loader2, Crown, Trophy, XCircle, Clock, Ban,
} from 'lucide-react';
import { loggedGet, loggedMutation, apiClient } from './loggedFetch';
import logger from '../../lib/logger';
import DisplacementReportModal from './DisplacementReportModal';
import LeadershipRollup from './LeadershipRollup';
import StepChecklist from './StepChecklist';
import OutcomeModal from './OutcomeModal';
import './OutcomeModal.css';

const COLUMNS = [
  { key: 'backlog', label: 'Backlog' },
  { key: '30', label: '30 days' },
  { key: '60', label: '60 days' },
  { key: '90', label: '90 days' },
];

function formatCurrencyShort(num) {
  if (num == null) return '';
  const n = Number(num) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

const OUTCOME_ICONS = {
  won: Trophy,
  lost: XCircle,
  deferred: Clock,
  disqualified: Ban,
};

const OUTCOME_LABELS = {
  won: 'Won',
  lost: 'Lost',
  deferred: 'Deferred',
  disqualified: 'Disqualified',
};

function OutcomeBadge({ outcome_status }) {
  if (!outcome_status || outcome_status === 'open') return null;
  const Icon = OUTCOME_ICONS[outcome_status];
  return (
    <span className={`vca-outcome-badge vca-outcome-badge--${outcome_status}`}>
      {Icon && <Icon size={10} />}
      <span>{OUTCOME_LABELS[outcome_status]}</span>
    </span>
  );
}

function ReportBadge({ card, onView, onRetry }) {
  const status = card.report_status || 'none';

  if (status === 'generating') {
    return (
      <div className="vca-report-badge vca-report-badge--generating">
        <Loader2 size={12} className="vca-spin" />
        <span>Generating CIO brief…</span>
      </div>
    );
  }
  if (status === 'ready') {
    return (
      <button
        type="button"
        className="vca-report-badge vca-report-badge--ready"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onView}
        title="Open the Master-Architect-level executive briefing"
      >
        <FileText size={12} />
        <span>View executive brief</span>
      </button>
    );
  }
  if (status === 'failed') {
    return (
      <button
        type="button"
        className="vca-report-badge vca-report-badge--failed"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onRetry}
        title={card.report_error || 'Report failed — retry'}
      >
        <AlertTriangle size={12} />
        <span>Brief failed · retry</span>
      </button>
    );
  }
  // 'none' — surface a manual generate button.
  return (
    <button
      type="button"
      className="vca-report-badge vca-report-badge--idle"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={onRetry}
      title="Generate the Master-Architect CIO briefing for this play"
    >
      <Sparkles size={12} />
      <span>Generate executive brief</span>
    </button>
  );
}

function SortableCard({ card, vendor, onUpdate, onDelete, onViewReport, onGenerateReport, onRecordOutcome, columnKey }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: card.id });
  const style = {
    transform: dndCSS.Transform.toString(transform),
    transition,
  };

  const hasOutcome = card.outcome_status && card.outcome_status !== 'open';
  const is90Day = columnKey === '90';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`vca-card ${isDragging ? 'dragging' : ''} ${hasOutcome ? 'has-outcome' : ''}`}
      {...attributes}
      {...listeners}
    >
      <div className="vca-card__title">
        {card.title}
        <OutcomeBadge outcome_status={card.outcome_status} />
      </div>
      <div className="vca-card__meta">
        {card.dollar_impact != null && <span>{formatCurrencyShort(card.dollar_impact)}</span>}
        {card.due_date && <span>{new Date(card.due_date).toLocaleDateString()}</span>}
      </div>
      {card.rationale && (
        <div className="vca-snapshot-card__sub" style={{ fontSize: '0.78rem' }}>
          {card.rationale}
        </div>
      )}
      <ReportBadge
        card={card}
        onView={() => onViewReport(card)}
        onRetry={() => onGenerateReport(card.id, { regenerate: card.report_status === 'failed' })}
      />
      <StepChecklist
        vendor={vendor}
        cardId={card.id}
        steps={card.steps || []}
        stepsDone={card.steps_done || 0}
        stepsTotal={card.steps_total || 0}
        compact
      />
      <textarea
        className="vca-card__notes"
        placeholder="Notes & account intel — typed here feeds into the next CIO briefing"
        defaultValue={card.notes || ''}
        onPointerDown={(e) => e.stopPropagation()}
        onBlur={(e) => {
          const next = e.target.value;
          if (next !== (card.notes || '')) onUpdate(card.id, { notes: next });
        }}
      />
      <div className="vca-card__actions">
        {/* Show Record Outcome button for 90-day cards */}
        {is90Day && onRecordOutcome && (
          <button
            className={`vca-add-btn vca-outcome-btn ${hasOutcome ? 'vca-outcome-btn--done' : ''}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onRecordOutcome(card)}
            title={hasOutcome ? 'Update outcome' : 'Record the final outcome of this play'}
          >
            <Trophy size={12} />
            <span>{hasOutcome ? 'Update outcome' : 'Record outcome'}</span>
          </button>
        )}
        <button
          className="vca-card__delete"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onDelete(card.id)}
        >
          <X size={12} /> Remove
        </button>
      </div>
    </div>
  );
}

function DroppableColumn({ column, cards, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `col-${column.key}`,
    data: { columnKey: column.key },
  });
  return (
    <div
      ref={setNodeRef}
      className="vca-kanban-col"
      style={isOver ? { boxShadow: '0 0 0 2px rgba(79,70,229,0.4)' } : undefined}
    >
      <div className="vca-kanban-col__head">
        <span>{column.label}</span>
        <span className="vca-kanban-col__count">{cards.length}</span>
      </div>
      {children}
    </div>
  );
}

function PlanKanban({ vendor, includeLowConfidence, strict }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [reportCard, setReportCard] = useState(null);
  const [rollupOpen, setRollupOpen] = useState(false);
  const [outcomeCard, setOutcomeCard] = useState(null);

  // Track which card IDs we've already auto-triggered so React-Query
  // refetches don't re-fire the Opus call.
  const autoTriggeredRef = useRef(new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-analysis', 'plan', vendor],
    queryFn: loggedGet('PlanKanban:fetch', `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/plan`),
    enabled: !!vendor,
    // Poll briefly while reports are generating so the badge flips to
    // "ready" without manual refresh.
    refetchInterval: (query) => {
      const cards = query.state.data?.cards || [];
      return cards.some(c => c.report_status === 'generating') ? 4000 : false;
    },
  });

  const generateReport = useMutation({
    mutationFn: loggedMutation(
      'PlanKanban:generate-report',
      ({ cardId, regenerate }) =>
        apiClient.post(
          `/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards/${cardId}/report${regenerate ? '?regenerate=true' : ''}`,
          {}
        ),
    ),
    onMutate: ({ cardId }) => {
      // Optimistically set status so the badge flips immediately.
      queryClient.setQueryData(['vendor-analysis', 'plan', vendor], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          cards: prev.cards.map(c =>
            c.id === cardId ? { ...c, report_status: 'generating', report_error: null } : c
          ),
        };
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan', vendor] });
    },
    onError: (err, vars) => {
      logger.vendorAnalysis.warn('PlanKanban:generate-report:err', {
        card_id: vars?.cardId, message: err?.message,
      });
    },
  });

  const addCard = useMutation({
    mutationFn: loggedMutation(
      'PlanKanban:add',
      (body) => apiClient.post(`/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards`, body),
    ),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan', vendor] });
      // Auto-fire executive brief generation for newly-added cards that
      // carry seed context (i.e. came from "Add to plan" buttons on the
      // displacement table or renewal calendar). Manually-typed cards
      // with no seed_payload skip the Opus call — generic titles like
      // "Reach out to CDT" don't justify the spend.
      if (created?.id && created?.seed_payload) {
        if (!autoTriggeredRef.current.has(created.id)) {
          autoTriggeredRef.current.add(created.id);
          generateReport.mutate({ cardId: created.id, regenerate: false });
        }
      }
    },
  });

  const updateCard = useMutation({
    mutationFn: loggedMutation(
      'PlanKanban:update',
      ({ id, body }) => apiClient.put(`/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards/${id}`, body),
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan', vendor] }),
  });

  const deleteCard = useMutation({
    mutationFn: loggedMutation(
      'PlanKanban:delete',
      (id) => apiClient.del(`/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards/${id}`),
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan', vendor] }),
  });

  // Backfill: any card with seed_payload and report_status='none' that we
  // haven't already auto-triggered gets a one-time auto-fire. Covers
  // cards seeded before this feature shipped, and cards created in
  // another tab.
  useEffect(() => {
    if (!data?.cards || !vendor) return;
    for (const card of data.cards) {
      if (
        card.seed_payload &&
        card.report_status === 'none' &&
        !autoTriggeredRef.current.has(card.id)
      ) {
        autoTriggeredRef.current.add(card.id);
        generateReport.mutate({ cardId: card.id, regenerate: false });
      }
    }
  // eslint-disable-next-line
  }, [data?.cards?.length, vendor]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const cardsByColumn = useMemo(() => {
    const m = { backlog: [], 30: [], 60: [], 90: [] };
    for (const c of (data?.cards || [])) {
      if (m[c.column_key]) m[c.column_key].push(c);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => a.position - b.position);
    return m;
  }, [data]);

  const findCardColumn = (cardId) => {
    for (const k of Object.keys(cardsByColumn)) {
      if (cardsByColumn[k].some(c => c.id === cardId)) return k;
    }
    return null;
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;

    const fromCol = findCardColumn(active.id);
    if (!fromCol) return;

    let toCol = null;
    if (typeof over.id === 'string' && over.id.startsWith('col-')) {
      toCol = over.id.slice(4);
    } else {
      toCol = findCardColumn(over.id);
    }
    if (!toCol) return;

    const fromCards = cardsByColumn[fromCol];
    const toCards = cardsByColumn[toCol];
    const fromIdx = fromCards.findIndex(c => c.id === active.id);
    const overIdx = typeof over.id === 'number' ? toCards.findIndex(c => c.id === over.id) : toCards.length;

    let newPos;
    if (fromCol === toCol) {
      const reordered = arrayMove(fromCards, fromIdx, overIdx);
      const idx = reordered.findIndex(c => c.id === active.id);
      const before = reordered[idx - 1]?.position;
      const after = reordered[idx + 1]?.position;
      newPos = computePosition(before, after);
      updateCard.mutate({ id: active.id, body: { position: newPos } });
    } else {
      const before = toCards[overIdx - 1]?.position;
      const after = toCards[overIdx]?.position;
      newPos = computePosition(before, after);
      updateCard.mutate({ id: active.id, body: { column_key: toCol, position: newPos } });
    }
  };

  const handleAdd = (columnKey) => {
    if (!draftTitle.trim()) { setAdding(null); return; }
    addCard.mutate({ column_key: columnKey, title: draftTitle.trim() });
    setAdding(null);
    setDraftTitle('');
  };

  if (isLoading) return <div className="vca-section vca-section__loading">Loading plan…</div>;
  if (error) return <div className="vca-section vca-section__error">Plan failed: {error.message}</div>;

  const totalCards = (data?.cards || []).length;
  const readyCount = (data?.cards || []).filter(c => c.report_status === 'ready').length;

  return (
    <div className="vca-section">
      <div className="vca-section-head">
        <div>
          <h2>30-60-90 plan for {vendor}</h2>
          <p className="vca-section__sub">
            Drag cards between columns. Notes save on blur. Cards seeded
            from "Add to plan" auto-generate a Master-Architect-level
            CIO briefing with Claude Opus 4.7 — click
            {' '}<strong>View executive brief</strong> when ready.
          </p>
          {/* Plan cards are user-authored — the shell time filter cannot
              scope them, so say so instead of implying it does. */}
          <span className="vca-nofilter-note">Not affected by the global time filter</span>
        </div>
        {totalCards > 0 && (
          <button
            type="button"
            className="vca-add-btn vca-rollup-btn"
            onClick={() => setRollupOpen(true)}
            title="Open the leadership roll-up: state-wide footprint + every plan card with its CIO briefing, ready to print or paste into a deck."
          >
            <Crown size={14} />
            <span>Leadership roll-up</span>
            <span className="vca-rollup-count">
              {readyCount}/{totalCards}
            </span>
          </button>
        )}
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
        <div className="vca-kanban">
          {COLUMNS.map(col => {
            const cards = cardsByColumn[col.key];
            return (
              <DroppableColumn key={col.key} column={col} cards={cards}>
                <SortableContext items={cards.map(c => c.id)} strategy={verticalListSortingStrategy}>
                  {cards.map(card => (
                    <SortableCard
                      key={card.id}
                      card={card}
                      vendor={vendor}
                      columnKey={col.key}
                      onUpdate={(id, body) => updateCard.mutate({ id, body })}
                      onDelete={(id) => deleteCard.mutate(id)}
                      onViewReport={(c) => setReportCard(c)}
                      onGenerateReport={(cardId, opts) =>
                        generateReport.mutate({ cardId, regenerate: !!opts?.regenerate })
                      }
                      onRecordOutcome={(c) => setOutcomeCard(c)}
                    />
                  ))}
                </SortableContext>
                {adding === col.key ? (
                  <div className="vca-card">
                    <input
                      autoFocus
                      value={draftTitle}
                      onChange={(e) => setDraftTitle(e.target.value)}
                      placeholder="Card title…"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAdd(col.key);
                        if (e.key === 'Escape') { setAdding(null); setDraftTitle(''); }
                      }}
                      style={{ width: '100%' }}
                    />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="vca-add-btn" onClick={() => handleAdd(col.key)}>Add</button>
                      <button
                        className="vca-card__delete"
                        onClick={() => { setAdding(null); setDraftTitle(''); }}
                      >Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button className="vca-add-card" onClick={() => setAdding(col.key)}>
                    <Plus size={14} /> Add card
                  </button>
                )}
              </DroppableColumn>
            );
          })}
        </div>
      </DndContext>

      <DisplacementReportModal
        isOpen={!!reportCard}
        vendor={vendor}
        card={reportCard}
        onClose={() => setReportCard(null)}
        onRegenerate={(cardId) => {
          generateReport.mutate({ cardId, regenerate: true });
        }}
      />

      <LeadershipRollup
        isOpen={rollupOpen}
        vendor={vendor}
        onClose={() => setRollupOpen(false)}
        includeLowConfidence={includeLowConfidence}
        strict={strict}
      />

      <OutcomeModal
        isOpen={!!outcomeCard}
        vendor={vendor}
        card={outcomeCard}
        onClose={() => setOutcomeCard(null)}
        onSuccess={() => {
          logger.vendorAnalysis.info('PlanKanban:outcome-recorded', {
            vendor, card_id: outcomeCard?.id,
          });
        }}
      />
    </div>
  );
}

function computePosition(before, after) {
  if (before == null && after == null) return 1024;
  if (before == null) return after - 512;
  if (after == null) return before + 512;
  return (before + after) / 2;
}

export default PlanKanban;
